import {PlotData} from 'plotly.js';

import {BarColors} from './colors';
import {Section as Query} from './query';
import {Section as _Config} from './types';

type Config = _Config['details']['snapshot']['chart'];

interface Breaks {
	low: number;
	high: number;
}

interface Grouped {
	color: string;
	snaps: {
		binMin: number;
		binMax: number;
		population: number;
	}[];
	hoverText?: string[];
}

const groupByColor = (query: Query, breaks: Breaks, reverse?: boolean) => {
	const greens = [];
	const yellows = [];
	const reds = [];

	for (const snap of query.snapshots) {
		const low = snap.binMax < breaks.low;
		const high = snap.binMin > breaks.high;
		const good = !reverse ? low : high;
		const bad = !reverse ? high : low;

		if (good) {
			greens.push(snap);
		} else if (bad) {
			reds.push(snap);
		} else {
			yellows.push(snap);
		}
	}

	return [
		{color: BarColors.green, snaps: greens, hoverText: []},
		{color: BarColors.yellow, snaps: yellows, hoverText: []},
		{color: BarColors.red, snaps: reds, hoverText: []}
	];
};

const getBreaks = (query: Query): Breaks => {
	const template = query.historicals.find((h) => h.age === 0);
	if (template) {
		return {
			low: template.breakpoints[1],
			high: template.breakpoints[2],
		};
	}

	return {
		low: Number.MAX_SAFE_INTEGER,
		high: Number.MAX_SAFE_INTEGER,
	};
};

const getMarker = (value: number, height: number, text: string): Partial<PlotData> => {
	return {
		mode: 'text+lines',
		marker: {
			color: BarColors.marker
		},
		y: [0, height],
		x: [value, value],

		hoverinfo: 'skip',
		texttemplate: ['', 'Your result: ' + text],
		textposition: 'top center',

		showlegend: false,
	};
};

const getHoverSums = (grouped: Grouped[], total: number, reverse?: boolean) => {
	let runningSum = 0;
	for (const group of grouped) {
		const popTextGroup = [];
		if (!reverse) {
			for (const snap of group.snaps) {
				runningSum += snap.population;
				popTextGroup.push((runningSum / total * 100).toString());
			}
		} else {
			for (let i = 1; i <= group.snaps.length; i++) {
				runningSum += group.snaps[group.snaps.length-i].population;
				popTextGroup.unshift(((total - runningSum) / total * 100).toString());
			}
		}
		group.hoverText = popTextGroup;
	}
};

export const useSnapshot = (query: Query, config: Config, reverse?: boolean) => {
	const {total, max} = query.snapshots.reduce((acc, curr) => ({
		total: acc.total + curr.population,
		max: Math.max(acc.max, curr.population),
	}), {
		total: 0,
		max: 0,
	});

	const grouped = groupByColor(query, getBreaks(query), reverse);
	getHoverSums(grouped, total, reverse);
	const bars = grouped.map((g): Partial<PlotData> => ({
		type: 'bar',
		x: g.snaps.map((s) => s.binMin),
		y: g.snaps.map((s) => s.population / total * 100),
		text: g.hoverText,
		textposition: 'none',
		marker: {
			color: g.color
		},
		hovertemplate: config.hover,
		name: '',
	}));

	return {
		snapshot: (index: number) => ({
			data: [
				...bars,
				getMarker(
					query.results[index].value,
					max / total * 100,
					config.marker,
				),
			],
			xlabel: config.xlabel,
		}),
	};
};
